import type NutrientViewer from '@nutrient-sdk/viewer';
import type { Configuration, RectangleAnnotation } from '@nutrient-sdk/viewer';
import PSPDFKit from 'pspdfkit';
import logger from 'src/logger';

export const PSPDFKIT_URL =
  window.Glide && window.Glide.PSPDFKIT_URL
    ? `${window.Glide.PSPDFKIT_URL}${
        window.Glide.PSPDFKIT_URL.endsWith('/') ? '' : '/'
      }`
    : undefined;

// Deprecating PSPDFKit; keeping only NutrientViewer.
type PSPDFKitPackage = typeof NutrientViewer | typeof PSPDFKit;

let cachedBaseConfig: Partial<Configuration> | undefined;
let cachedPSPDFKit: PSPDFKitPackage | undefined;

const buildBaseConfig = (
  pspdfkitSDK: PSPDFKitPackage,
  packageName: string
): Partial<Configuration> => {
  let baseUrl: string;
  if (!packageName || packageName === 'pspdfkit') {
    baseUrl = window.WEBPACK_PUBLIC_PATH;
  } else {
    baseUrl = `${window.WEBPACK_PUBLIC_PATH}${packageName}/`;
  }
  return {
    baseUrl,
    serverUrl: PSPDFKIT_URL,
    container: '#doc-viewer',
    instant: false,
    autoSaveMode: pspdfkitSDK.AutoSaveMode.DISABLED,
    editableAnnotationTypes: [
      pspdfkitSDK.Annotations.RectangleAnnotation as typeof RectangleAnnotation,
    ],
    maxDefaultZoomLevel: 2.4,
    minDefaultZoomLevel: 1,
  };
};

const setPspdfkitOptions = (pspdfkitSDK: PSPDFKitPackage) => {
  pspdfkitSDK.Options.IGNORE_DOCUMENT_PERMISSIONS = true;
  pspdfkitSDK.Options.SELECTION_OUTLINE_PADDING = () => -1;
  pspdfkitSDK.Options.SELECTION_STROKE_WIDTH = 2;
};

const importPSPDFKitPackage = async (version?: string) => {
  let packageName: string;
  let resolvedPackage: PSPDFKitPackage;
  switch (version) {
    case '2023.1':
      packageName = 'pspdfkit-2023.1.4';
      resolvedPackage = (await import(
        'pspdfkit-2023.1.4'
      )) as unknown as PSPDFKitPackage;
      break;
    case '2024.1':
    case 'document-engine-1.1':
      packageName = 'pspdfkit-2024.1.3';
      resolvedPackage = (await import(
        'pspdfkit-2024.1.3'
      )) as unknown as PSPDFKitPackage;
      break;
    case 'document-engine-1.6':
      packageName = 'nutrient-sdk-viewer';
      resolvedPackage = (await import(
        '@nutrient-sdk/viewer'
      )) as unknown as PSPDFKitPackage;
      break;
    case 'document-engine-1.15':
      packageName = 'nutrient-sdk-viewer-1.14.0';
      resolvedPackage = (await import(
        'nutrient-sdk-viewer-1.14.0'
      )) as unknown as PSPDFKitPackage;
      break;
    default:
      packageName = 'pspdfkit';
      resolvedPackage = (await import(
        'pspdfkit'
      )) as unknown as PSPDFKitPackage;
  }
  return { resolvedPackage, packageName };
};

export const initializePSPDFKit = async (version?: string) => {
  if (cachedPSPDFKit && cachedBaseConfig) {
    return undefined;
  }
  const {
    resolvedPackage: { default: pspdfkitSDK },
    packageName,
  } = await importPSPDFKitPackage(version);

  const versionBaseConfig = buildBaseConfig(pspdfkitSDK, packageName);
  setPspdfkitOptions(pspdfkitSDK);

  cachedPSPDFKit = pspdfkitSDK;
  cachedBaseConfig = versionBaseConfig;
  return {
    PSPDFKit: pspdfkitSDK,
    baseConfig: versionBaseConfig,
  };
};

export const getPSPDFKit = (): PSPDFKitPackage => {
  if (!cachedPSPDFKit) {
    setPspdfkitOptions(PSPDFKit);
    logger.error(
      '[getPSPDFKit] The dynamic PSPDFKit SDK was not initialized. Fallback to the default SDK.'
    );
    return PSPDFKit;
  }
  return cachedPSPDFKit;
};

export const getBaseConfig = (): Partial<Configuration> => {
  if (!cachedBaseConfig) {
    logger.error(
      '[getBaseConfig] The dynamic PSPDFKit SDK was not initialized. Fallback to the default SDK.'
    );
    return buildBaseConfig(PSPDFKit, 'pspdfkit');
  }
  return cachedBaseConfig;
};

export const DEFAULT_ZOOM_LEVEL = 'FIT_TO_WIDTH';

/**
 * Type for contentDocument which can be either Document (older versions) or ShadowRoot (document-engine-1.6+)
 */
type PSPDFKitContentDoc = Document | ShadowRoot;

/**
 * Creates an element in contentDocument (Document or ShadowRoot).
 * ShadowRoot doesn't have createElement, so we use the host's ownerDocument.
 *
 * @param contentDoc The contentDocument from PSPDFKit instance
 * @param tagName The HTML tag name to create
 * @returns The created HTMLElement
 */
export function createElementInContentDoc(
  contentDoc: PSPDFKitContentDoc,
  tagName: string
): HTMLElement {
  if (contentDoc instanceof ShadowRoot) {
    return contentDoc.ownerDocument.createElement(tagName);
  }
  return (contentDoc as Document).createElement(tagName);
}

/**
 * Appends a child node to the contentDocument (inside shadow DOM for ShadowRoot, inside document for Document).
 * - Document: Appends to body (inside iframe)
 * - ShadowRoot: Appends to ShadowRoot itself (inside shadow DOM)
 *
 * @param contentDoc The contentDocument from PSPDFKit instance
 * @param child The node to append
 */
export function appendToContentDoc(
  contentDoc: PSPDFKitContentDoc,
  child: Node
): void {
  if (contentDoc instanceof ShadowRoot) {
    contentDoc.appendChild(child);
  } else {
    (contentDoc as Document).body.appendChild(child);
  }
}

/**
 * Gets the host HTMLElement for contentDocument (used for layout operations).
 * - Document: Returns body
 * - ShadowRoot: Returns host element (#doc-viewer)
 *
 * @param contentDoc The contentDocument from PSPDFKit instance
 * @returns The host HTMLElement for layout operations
 */
export function getContentDocHostElement(
  contentDoc: PSPDFKitContentDoc
): HTMLElement {
  if (contentDoc instanceof ShadowRoot) {
    return contentDoc.host as HTMLElement;
  }
  return (contentDoc as Document).body;
}
